import { Component }  from "react";
import { Link, RouteComponentProps, withRouter } from "react-router-dom";
import { QueryClient, QueryClientContext } from "@tanstack/react-query";
import { EditProject } from "./EditProject";
import { ResourceStatus, ResourceType } from "@app/resources";
import JobsPanelContent from "./JobsPanelContent";
import { FlyoutLink } from "@app/shared/Flyouts";
import { ModalLink } from "@app/shared/Modals";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";
import { breadcrumbTemplates, BreadcrumbTemplateEnum } from "@app/shared/breadcrumbs/breadcrumbTemplates";
import { CountyProjectContext } from "@app/projects/CountyProjectContext";
import ProjectOptions from "@app/projects/projectOptions";
import RoleRestricted from "@app/user/RoleRestricted";
import Roles from "@app/user/roles";
import { UserInfo } from "@app/user/UserContext";
import ResourceService from "@app/resources/resourceService";
import ProviderActionsDrawerFlyout from "@app/projects/network-assessment/provider-actions/ProviderActionsDrawerFlyout";

interface ResourceControlsProps extends RouteComponentProps {
  defaultProjectOptionsOpen: boolean;
  isArchived: boolean;
  isProjectRunning: boolean;
  isReadOnly: boolean;
  isShared: boolean;
  resourceId: number;
  projectName: string;
  user: UserInfo;
}

interface ResourceControlState {
  projectOptionsDirty: boolean;
  sidePanelArchiveWarningVisible: boolean;
}

class ResourceControls extends Component<ResourceControlsProps, ResourceControlState> {
  private readonly resourceService = new ResourceService();

  constructor(props: ResourceControlsProps) {
    super(props);

    this.state = {
      projectOptionsDirty: false,
      sidePanelArchiveWarningVisible: false,
    };
  }

  public render() {
    const archiveText = this.props.isArchived ? "Unarchive" : "Archive";

    return (
      <CountyProjectContext.Consumer>
        {({
          project,
          runProject,
          forceUpdateProject,
          setProjectReadOnlyProperties,
        }) => (
          <QueryClientContext.Consumer>
            {(queryClient) => (
              <>
                <FlyoutLink
                  content={{
                    body: this.renderProjectOptions(
                      project.projectId,
                      runProject,
                      setProjectReadOnlyProperties,
                      forceUpdateProject,
                      queryClient,
                    ),
                    title: "Project Options",
                  }}
                  modalContent={{
                    body: "You will lose unsaved changes. Continue with closing the sidebar?",
                    cancelButtonText: "Cancel",
                    confirmButtonText: "Continue",
                    title: "Warning",
                  }}
                  isVisible={this.props.defaultProjectOptionsOpen}
                  size="80"
                  warnOnClose={this.state.projectOptionsDirty}
                  testId="resource-controls__project-options-link"
                  onClose={() => {
                    this.setState({ projectOptionsDirty: false });
                  }}
                >
                  <span className="project-action-button">
                    <QesIcon iconName={QesIcons.PencilV1} description="Edit Project Options" />
                    {this.props.isShared ? " " : " Edit "}Project Options
                  </span>
                </FlyoutLink>

                <ProviderActionsDrawerFlyout
                  classNames="ml15"
                  disabled={this.props.isReadOnly}
                  testId="resource-controls__provider-actions-flyout-link"
                >
                  <span className={`project-action-button ${this.props.isReadOnly ? " disabled" : ""}`}>
                    <QesIcon iconName={QesIcons.Zap} description="Provider Action Management" /> Provider Action Management
                  </span>
                </ProviderActionsDrawerFlyout>

                {!this.props.isShared && (
                  <>
                    <span className="ml15">
                      <Link
                        to={{
                          pathname: `/resources/share`,
                          state: {
                            breadcrumbItems: [
                              ...breadcrumbTemplates[
                                BreadcrumbTemplateEnum.NetworkAssessment
                              ],
                              {
                                link: this.props.location.pathname,
                                text: this.props.projectName,
                              },
                            ],
                            isSingleResource: true,
                            resourceDescription: "Project",
                            resourceId: this.props.resourceId,
                            resourceTypes: [ResourceType.Project],
                          },
                        }}
                      >
                        <span className="project-action-button">
                          <QesIcon iconName={QesIcons.ShareV6} description="Share Project" /> Share Project
                        </span>
                      </Link>
                    </span>
                    <ModalLink
                      classNames="ml15 side-panel-link"
                      disabled={
                        this.props.isProjectRunning ||
                        (this.props.isReadOnly && !this.props.isArchived)
                      }
                      content={{
                        body: `${archiveText} this project?`,
                        cancelButtonText: "Cancel",
                        confirmButtonAction: this.handleArchiveConfirm,
                        confirmButtonText: archiveText,
                        confirmButtonType:
                          archiveText === "Archive" ? "primary-danger" : "primary",
                        title: archiveText,
                      }}
                      testId="resource-controls__archive-project-modal-link"
                    >
                      <span className={`project-action-button ${this.props.isProjectRunning || (this.props.isReadOnly && !this.props.isArchived) ? " disabled" : ""}`}>
                        <QesIcon iconName={QesIcons.Archive} description={`${archiveText} Project`} /> {archiveText} Project
                      </span>
                    </ModalLink>
                    <span className="ml15">
                      <Link
                        to={{
                          pathname: `/projects/${this.props.resourceId}/clone`,
                          state: {
                            linkToProject: true,
                          },
                        }}
                      >
                        <span className="project-action-button">
                          <QesIcon iconName={QesIcons.CopyV7} description="Clone Project" /> Clone Project
                        </span>
                      </Link>
                    </span>
                    <RoleRestricted
                      allowedRoles={[Roles.QUEST_USER, Roles.QUEST_ADMIN]}
                    >
                      <FlyoutLink
                        content={{
                          body: (
                            <JobsPanelContent
                              projectId={project.projectId}
                              projectName={project.resource.name}
                              user={this.props.user}
                            />
                          ),
                          title: `Jobs for Project ${project.projectId}: ${project.resource.name}`,
                        }}
                        testId="resource-controls__project-jobs-flyout-link"
                        classNames="ml15"
                        size="60"
                      >
                        <span className="project-action-button">
                          <QesIcon iconName={QesIcons.TerminalBrowser} description="Jobs for Project" /> Jobs
                        </span>
                      </FlyoutLink>
                    </RoleRestricted>
                  </>
                )}
              </>
            )}
          </QueryClientContext.Consumer>
        )}
      </CountyProjectContext.Consumer>
    );
  }

  private handleArchiveConfirm = async () => {
    if (!this.props.isProjectRunning) {
      if (this.props.isArchived) {
        await this.resourceService.updateResourceArchiveStatus(this.props.resourceId, ResourceStatus.Active);
      } else {
        await this.resourceService.updateResourceArchiveStatus(this.props.resourceId, ResourceStatus.Archived);
      }

      window.location.href = `${window.customUIBaseUrl}/`;
    }
  }

  private handleProjectOptionsChange = (projectOptionsDirty: boolean) => {
    if (this.state.projectOptionsDirty !== projectOptionsDirty) {
      this.setState({ projectOptionsDirty });
    }
  }

  private renderProjectOptions(
    projectId: number,
    runProject: () => Promise<void>,
    setProjectReadOnlyProperties: (isReadOnly: boolean, isEditRestricted: boolean, delay?: boolean) => ProjectOptions,
    forceUpdateProject?: () => Promise<void>,
    queryClient?: QueryClient,
  ) {
    return (
      <EditProject
        disabled={this.props.isShared}
        onChange={this.handleProjectOptionsChange}
        projectId={projectId}
        onRunProject={async () => {
          if (runProject) {
            await setProjectReadOnlyProperties(true, true);
            await runProject();
          }
        }}
        onSubmitSuccess={async () => {
          if (forceUpdateProject) {
            await forceUpdateProject();
          }
          await queryClient?.invalidateQueries({
            predicate: (query) => query.queryKey[0] === "project" && String(query.queryKey[2]) === String(projectId),
          });
        }}
      />
    );
  }
}

export default withRouter(ResourceControls);
